<script setup>
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { usePlayers } from '../composables/usePlayers.js';
import { useFileActions } from '../composables/useFileActions.js';
import { FEET, POSITION_TBD, positionsByGroup } from '../domain/positions.js';
import { STATUS_ORDER } from '../domain/status.js';
import { AGE_GROUPS } from '../domain/ageGroups.js';
import { validateBirthDate, birthDateBounds, MIN_BIRTH_YEAR } from '../domain/birthDate.js';
import CountrySelect from './CountrySelect.vue';
import DateField from './DateField.vue';

/* Positionsauswahl gruppiert wie in der Kaderliste; Vorgabe bleibt "Position TBD". */
const positionGroups = positionsByGroup();

const open = defineModel({ type: Boolean, default: false });

const { t } = useI18n();
const { save, createDraft, load } = usePlayers();
const { upload } = useFileActions();

const draft = ref(createDraft());
const photoFile = ref(null);
const photoPreview = ref(null);
const photoInput = ref(null);
const busy = ref(false);
const error = ref(null);

/* Geburtsdatum ist beim Anlegen Pflicht (domain/birthDate.js). Die Meldung erscheint erst
   nach dem ersten Versuch zu speichern oder nach Verlassen des Feldes — nicht schon beim
   Öffnen des leeren Formulars — und folgt danach der Eingabe. Nur hier, nicht beim
   Bearbeiten bestehender Akten (SectorPass). */
const dobTouched = ref(false);
const dobInput = ref(null);
/* Getippter Text von DateField (TT.MM.JJJJ). Eine halbe Eingabe ("15.03") ergibt noch kein ISO-Datum,
   ist aber nicht "leer" — sie wird als ungültig gemeldet, nicht als fehlend. */
const dobText = ref('');
/* DateField liefert nur echte Daten innerhalb von min/max (Geburtsdatum: 1950 bis heute) und sagt sonst, warum nicht:
   tooLate = Zukunft, tooEarly = vor 1950. Die Meldungen bleiben die der Geburtsdatum-Prüfung (newPlayer.dob.*). */
const dobBounds = birthDateBounds();
const dobFieldProblem = ref('');
const FIELD_TO_DOB_PROBLEM = { tooLate: 'future', tooEarly: 'tooOld' };
const dobProblem = computed(() => {
  if (draft.value.dob) return validateBirthDate(draft.value.dob);
  return dobText.value ? FIELD_TO_DOB_PROBLEM[dobFieldProblem.value] || 'invalid' : 'required';
});
const dobError = computed(() => (dobTouched.value ? dobProblem.value : null));

/* Ein getipptes, aber falsches Datum sperrt "Create player" bis zur Korrektur; ein leeres Feld nicht —
   dort erscheint die Meldung erst beim Versuch zu speichern (wie bisher). */
const canSubmit = computed(
  () =>
    draft.value.firstName.trim() &&
    draft.value.lastName.trim() &&
    !busy.value &&
    !(dobText.value && dobProblem.value)
);

function reset() {
  draft.value = createDraft();
  photoFile.value = null;
  photoPreview.value = null;
  error.value = null;
  dobTouched.value = false;
  dobText.value = '';
}

function close() {
  open.value = false;
  reset();
}

/* Das Bild wird erst nach dem Anlegen hochgeladen — der Speicherpfad enthält
   die Spieler-Kennung, und die gibt es vorher noch nicht. Bis dahin nur eine
   lokale Vorschau. */
function onPickPhoto(event) {
  const file = event.target.files?.[0];
  event.target.value = '';
  if (!file) return;
  photoFile.value = file;
  photoPreview.value = URL.createObjectURL(file);
}

function removePhoto() {
  photoFile.value = null;
  photoPreview.value = null;
}

async function submit() {
  if (!canSubmit.value) return;
  dobTouched.value = true;
  if (dobProblem.value) {
    dobInput.value?.focus();
    return;
  }
  busy.value = true;
  error.value = null;

  try {
    /* jg wird beim Speichern aus dob nachgezogen (supabase/players.js, domain/birthYear.js). */
    if (draft.value.height && !/cm/i.test(draft.value.height)) {
      draft.value.height = `${draft.value.height.trim()} cm`;
    }

    let saved = await save(draft.value);

    if (photoFile.value) {
      const stored = await upload(photoFile.value, `avatars/${saved.id}`);
      saved = await save({ ...saved, avatarPath: stored.path });
    }

    close();
    /* Neu laden, damit Sortierung und Gruppierung stimmen. */
    await load();
  } catch (e) {
    error.value = e.message || 'saveFailed';
  } finally {
    busy.value = false;
  }
}
</script>

<template>
  <template v-if="open">
    <div class="np-scrim" @click="close"></div>

    <div class="np-modal" role="dialog" aria-modal="true">
      <div class="modal-head">
        <h3>{{ $t('newPlayer.title') }}</h3>
      </div>

      <div class="modal-body">
        <div class="np-photo">
          <div class="np-photo-wrap">
            <button
              type="button"
              class="np-photo-box"
              :class="{ 'has-photo': !!photoPreview }"
              data-testid="np-photo-box"
              :title="photoPreview ? $t('player.changePhoto') : $t('newPlayer.addPhoto')"
              :aria-label="photoPreview ? $t('player.changePhoto') : $t('newPlayer.photoPlaceholder')"
              @click="photoInput.click()"
            >
              <img v-if="photoPreview" :src="photoPreview" alt="" />
              <span v-else class="np-photo-empty">
                <!-- Umriss, nicht gefüllt -->
                <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                  <path d="M4 8.5A1.5 1.5 0 0 1 5.5 7h2l1.2-2h6.6l1.2 2h2A1.5 1.5 0 0 1 20 8.5v9a1.5 1.5 0 0 1-1.5 1.5h-13A1.5 1.5 0 0 1 4 17.5z" />
                  <circle cx="12" cy="13" r="3.2" />
                </svg>
                <span>{{ $t('newPlayer.photoPlaceholder') }}</span>
              </span>
            </button>
            <button
              v-if="photoPreview"
              type="button"
              class="np-photo-remove"
              data-testid="np-photo-remove"
              :title="$t('newPlayer.removePhoto')"
              :aria-label="$t('newPlayer.removePhoto')"
              @click="removePhoto"
            >
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" aria-hidden="true">
                <path d="M6 6l12 12M18 6L6 18" />
              </svg>
            </button>
          </div>
          <input ref="photoInput" type="file" accept="image/*" hidden @change="onPickPhoto" />
          <span class="np-photo-hint">{{ $t('newPlayer.photoOptional') }}</span>
        </div>

        <div class="edit-grid">
          <div class="field">
            <label>{{ $t('player.firstName') }} *</label>
            <input v-model="draft.firstName" type="text" placeholder="Peter" />
          </div>
          <div class="field">
            <label>{{ $t('player.lastName') }} *</label>
            <input v-model="draft.lastName" type="text" placeholder="Anieke" />
          </div>
          <div class="field">
            <label>{{ $t('player.position') }}</label>
            <select v-model="draft.pos">
              <option :value="POSITION_TBD">{{ $t('player.positionTbd') }}</option>
              <optgroup v-for="group in positionGroups" :key="group.key" :label="$t(`positionGroup.${group.key}`)">
                <option v-for="pos in group.positions" :key="pos" :value="pos">{{ pos }}</option>
              </optgroup>
            </select>
          </div>
          <div class="field">
            <label>{{ $t('player.secondPosition') }}</label>
            <select v-model="draft.pos2">
              <option value="">—</option>
              <optgroup v-for="group in positionGroups" :key="group.key" :label="$t(`positionGroup.${group.key}`)">
                <option v-for="pos in group.positions" :key="pos" :value="pos">{{ pos }}</option>
              </optgroup>
            </select>
          </div>
          <div class="field">
            <label>{{ $t('player.foot') }}</label>
            <select v-model="draft.foot">
              <option value="">—</option>
              <option v-for="f in FEET" :key="f" :value="f">{{ f }}</option>
            </select>
          </div>
          <div class="field">
            <label>{{ $t('player.status') }}</label>
            <select v-model="draft.status">
              <option v-for="s in STATUS_ORDER" :key="s" :value="s">{{ $t(`status.${s}`) }}</option>
            </select>
          </div>
          <div class="field">
            <label for="np-dob">{{ $t('player.dob') }} *</label>
            <DateField
              id="np-dob"
              ref="dobInput"
              v-model="draft.dob"
              v-model:text="dobText"
              v-model:problem="dobFieldProblem"
              :min="dobBounds.min"
              :max="dobBounds.max"
              :show-error="false"
              birth
              required
              :aria-invalid="dobError ? 'true' : undefined"
              :aria-describedby="dobError ? 'np-dob-error' : undefined"
              @blur="dobTouched = true"
            />
            <p v-if="dobError" id="np-dob-error" class="np-field-error" role="alert">
              {{ $t(`newPlayer.dob.${dobError}`, { year: MIN_BIRTH_YEAR }) }}
            </p>
          </div>
          <div class="field">
            <label>{{ $t('player.height') }}</label>
            <input v-model="draft.height" type="text" placeholder="180" />
          </div>
          <div class="field">
            <label>{{ $t('player.nationality') }}</label>
            <CountrySelect v-model="draft.nationality" />
          </div>
          <!-- Altersklasse: von Hand, keine Zuordnung nach Geburtsjahr; Werte aus domain/ageGroups.js wie in "Edit details" -->
          <div class="field" data-testid="age-group-new">
            <label>{{ $t('ageGroup.label') }}</label>
            <select v-model="draft.ageGroup" data-testid="age-group-select">
              <option value="">{{ $t('ageGroup.none') }}</option>
              <option v-for="g in AGE_GROUPS" :key="g" :value="g">{{ g }}</option>
            </select>
          </div>
          <div class="field">
            <label>{{ $t('player.atClubSince') }}</label>
            <DateField v-model="draft.atGrassrunnersSince" />
          </div>
        </div>

        <p v-if="error" class="np-error">{{ $t(`errors.${error}`) }}</p>
      </div>

      <div class="np-actions">
        <button class="btn ghost small" :disabled="busy" @click="close">
          {{ $t('common.cancel') }}
        </button>
        <button class="btn small" :disabled="!canSubmit" @click="submit">
          {{ busy ? $t('common.loading') : $t('newPlayer.create') }}
        </button>
      </div>
    </div>
  </template>
</template>

<style scoped>
.np-scrim {
  position: fixed;
  inset: 0;
  background: rgba(4, 6, 9, 0.62);
  z-index: 30;
}
.np-modal {
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 31;
  width: min(560px, calc(100vw - 32px));
  max-height: calc(100vh - 48px);
  overflow-y: auto;
  background: var(--panel);
  border: 1px solid var(--border);
  border-radius: 14px;
  box-shadow: 0 20px 60px var(--shadow-strong);
}
.np-photo {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  margin-bottom: 18px;
}
/* Eckiges Hochformat wie die Spielerfotos: 3:4, Radius wie das große Foto im Profil (16px) */
.np-photo-wrap {
  position: relative;
  width: 108px;
}
.np-photo-box {
  display: block;
  width: 100%;
  aspect-ratio: 3 / 4;
  padding: 0;
  overflow: hidden;
  cursor: pointer;
  border-radius: 16px;
  border: 1.5px dashed var(--dim);
  background: var(--card);
  color: var(--muted);
}
.np-photo-box:hover {
  border-color: var(--green-dim);
}
.np-photo-box:focus-visible {
  outline: 2px solid var(--green);
  outline-offset: 3px;
}
.np-photo-box.has-photo {
  border-style: solid;
  background: transparent;
}
.np-photo-box img {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: top;
}
.np-photo-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  height: 100%;
  padding: 0 8px;
  font-size: 0.78rem;
  line-height: 1.3;
  text-align: center;
}
.np-photo-remove {
  position: absolute;
  top: 6px;
  right: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  padding: 0;
  border-radius: 50%;
  border: 1px solid var(--border);
  background: var(--card);
  color: var(--text);
  cursor: pointer;
}
.np-photo-remove:hover {
  color: var(--green);
  border-color: var(--green-dim);
}
.np-photo-hint {
  font-size: 0.72rem;
  color: var(--dim);
}
.np-field-error {
  margin: 2px 0 0;
  font-size: 0.74rem;
  line-height: 1.4;
  color: var(--red);
}
.np-error {
  margin-top: 12px;
  font-size: 0.8rem;
  color: var(--red);
}
.np-actions {
  display: flex;
  gap: 8px;
  justify-content: flex-end;
  padding: 14px 20px 18px;
  border-top: 1px solid var(--border-soft);
}
.btn:disabled {
  opacity: 0.5;
  cursor: default;
}
</style>
